import React, { useCallback, useRef, useState } from 'react';
import { GenericConfirmDialog } from '@/styles/GenericConfirmDialog';
import { addProjectMembers, checkProjectPerson } from '@/features/project/info/projectAccessApi';

export interface MembershipPerson { id: string; name: string }

interface DialogState {
  title: string;
  message: string;
  confirmLabel: string;
  /** true: nothing can be confirmed, the dialog only informs (one button) */
  informOnly: boolean;
  resolve: (confirmed: boolean) => void;
}

export const ADD_MEMBERS_MESSAGE = 'Are you sure you want to add the selected users as members of this project so they can access the documents assigned to them?';
export const ASK_ADMIN_MESSAGE = 'Some of the selected users are not members of this project yet. Ask a company admin to add them so they can access the documents assigned to them.';

/**
 * THE project-membership popup of every form with assigned people (vendor, client, assignee -- internal or external, or a members list). It is asked when the form is SAVED, never
 * when somebody is picked: `ensureMembers(projectId, people)` finds the people who are not members of the project yet and, if there are any, asks ONCE for all of them.
 *   - resolves true  -> nobody was missing, or the user confirmed and every missing person was added (in one request): the form can save;
 *   - resolves false -> the user cancelled, nobody here may add people, or the server refused: nothing was added and the form must not save.
 * The server stays the authority (it refuses an assignment to a non-member); this prompt grants nothing by itself. Render `prompt` once in the form.
 */
export function useProjectMembershipPrompt() {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const dialogRef = useRef<DialogState | null>(null);

  const ask = useCallback((state: Omit<DialogState, 'resolve'>) => new Promise<boolean>((resolve) => {
    dialogRef.current?.resolve(false); // a second Save while the first one is still asking never leaves a promise hanging
    const next = { ...state, resolve };
    dialogRef.current = next;
    setDialog(next);
  }), []);

  const close = useCallback((confirmed: boolean) => {
    const current = dialogRef.current;
    dialogRef.current = null;
    setDialog(null);
    current?.resolve(confirmed);
  }, []);

  /** The question itself, for a form that writes the members in its own save (ProjectForm): confirmed -> true; cancelled, or nobody here may add people -> false. */
  const confirmAddingPeople = useCallback(async (canManage: boolean, confirmLabel = 'Add and save'): Promise<boolean> => {
    if (!canManage) {
      await ask({ title: 'Add to project', message: ASK_ADMIN_MESSAGE, confirmLabel: 'OK', informOnly: true });
      return false;
    }
    return ask({ title: 'Add to project', message: ADD_MEMBERS_MESSAGE, confirmLabel, informOnly: false });
  }, [ask]);

  const ensureMembers = useCallback(async (projectId: string | null | undefined, people: Array<MembershipPerson | null | undefined>, confirmLabel?: string): Promise<boolean> => {
    const wanted = new Map<string, MembershipPerson>();
    for (const person of people) if (person?.id && person.id !== 'unassigned') wanted.set(person.id, person);
    if (!projectId || wanted.size === 0) return true;

    const answers = await Promise.all(Array.from(wanted.values()).map(async (person) => {
      try {
        return { person, status: await checkProjectPerson(projectId, person.id) };
      } catch {
        return { person, status: null }; // the server refuses a person outside the project when saving
      }
    }));
    const missing = answers.filter((answer) => answer.status && !answer.status.in_project);
    if (missing.length === 0) return true;

    if (!(await confirmAddingPeople(missing.every((answer) => answer.status?.can_manage), confirmLabel))) return false;
    try {
      // one request for everybody; it only ADDS (the server checks who may add whom)
      await addProjectMembers(projectId, missing.map((answer) => answer.person.id));
      return true;
    } catch (error) {
      await ask({ title: 'Could not add to project', message: error instanceof Error ? error.message : 'The selected users could not be added to the project.', confirmLabel: 'OK', informOnly: true });
      return false;
    }
  }, [ask, confirmAddingPeople]);

  const prompt = dialog ? (
    <GenericConfirmDialog
      isOpen
      title={dialog.title}
      message={dialog.message}
      confirmLabel={dialog.confirmLabel}
      hideCancel={dialog.informOnly}
      onConfirm={() => close(!dialog.informOnly)}
      onCancel={() => close(false)}
    />
  ) : null;

  return { ensureMembers, confirmAddingPeople, prompt };
}
